<template>
  <accordion-panel
    :id="installation.installationId"
    class="installation-panel"
    :class="{ 'installation-panel__no-collapse': isInstallationNormal() }"
    :collapsed.sync="collapseController"
    @update:collapsed="getAlarmDetails(collapse, false)"
    @shown="onShownHandler"
  >
    <template #title>
      <span class="installation-panel__installation-header-ico-lbl">
        <bolt-circle
          :color="getInstallationColor()"
          class="installation-panel__bolt-ico"
        />
        <span
          class="installation-panel__installation-name"
          :style="{ color: getInstallationColor() }"
        >
          <strong>{{ installation.installationName }}</strong>
          <span>| {{ getInstallationSummary() }}</span>
        </span>
      </span>
      <span class="installation-panel__installation-header-ico-btn">
        <span class="mx-2">
          <i
            aria-hidden="true"
            v-if="installation.disabled"
            class="fas fa-ban"
            v-b-tooltip.topleft="
              $t('alarms-and-warnings.notification-tooltip.disabled')
            "
          />
          <div
            v-if="installation.networkFailure"
            v-b-tooltip.topleft="
              $t('alarms-and-warnings.notification-tooltip.network-failure')
            "
            class="mb-1 installation-panel__installation-ico-network-container"
            alt=""
          >
            <svg class="mb-1 installation-panel__installation-ico-network-svg">
              <use
                xlink:href="static/imgs/inactive-network.svg#dynamicColor"
              ></use>
            </svg>
          </div>
          <i
            aria-hidden="true"
            v-if="installation.autoDiagnosis"
            class="fas fa-exclamation-circle"
            v-b-tooltip.topleft="
              $t('alarms-and-warnings.notification-tooltip.self-diagnosis')
            "
          />
          <info-badge
            v-if="installation.blocked"
            aria-hidden="true"
            translation-key="alarms-and-warnings.notification-tooltip.blocked"
          />
        </span>
      </span>
    </template>
    <s-table
      :fields="fields"
      :items="alarms"
      :busy="loading"
      class="installation-panel__table"
    >
      <template #cell(alarmClassification)="{ item }">
        <classification
          class="installation-panel__classification-icon"
          :classification="item.alarmClassification"
          show-label
          show-tooltip
        />
      </template>
      <template #cell(date)="{ item }">
        <span>
          {{ getFormattedAlarmDateTime(item) }}
        </span>
      </template>
      <template #cell(assetName)="{ item }">
        <span>
          {{ item.assetName }}
        </span>
      </template>
      <template #cell(alarmName)="{ item }">
        <span>
          {{ item.alarmName }}
        </span>
      </template>
      <template #cell(details)="{ item }">
        <b-button
          class="installation-panel__details-btn"
          variant="link"
          @click="openDetailsModal(item)"
          style="min-width: 110px;"
        >
          {{ $t('alarms-and-warnings.see-details') }}
          <i
            class="fas fa-arrow-right installation-panel__details-btn__details-arrow"
          />
        </b-button>
      </template>
      <template #cell(acted)="{ item }">
        <b-button
          class="installation-panel__alarm-btn"
          variant="link"
          :disabled="isRecognizeButtonDisabled"
          @click="openRecognizeModal(item)"
        >
          <icon-badge
            icon-class="fas fa-bell"
            badge-icon-class="fas fa-check"
            :icon-color="getAlarmColor(item)"
            :badge-color="getBadgeColor(item)"
            :show-badge="item.recognized"
          />
        </b-button>
      </template>
    </s-table>
    <details-alarm-modal
      ref="detailsAlarmModal"
      :alarm="modalAlarm"
      @open-recognize-modal="openRecognizeModal"
    />
    <recognize-alarm-modal
      ref="recognizeAlarmModal"
      :alarm="modalAlarm"
      @recognizedAlarm="onRecognizeAlarm"
    />
    <recognized-alarm-info-modal
      ref="recognizedAlarmInfoModal"
      :alarm="modalAlarm"
      @ignored-alarm="onRecognizeAlarm"
    />
  </accordion-panel>
</template>

<script>
import AccordionPanel from '@/components/AccordionPanel';
import BoltCircle from '@/components/icon/BoltCircle';
import AlarmsWarningsService from '@/domain/services/generalPanel/alarmsAndWarnings/general';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import LoadingMixin from '@/services/loadingMixin';
import RecognizeAlarmModal from '@/domain/components/generalPanel/alarmsAndWarnings/recognizeAlarmModal/index';
import DetailsAlarmModal from '@/domain/components/generalPanel/alarmsAndWarnings/detailsAlarmModal/index';
import RecognizedAlarmInfoModal from '@/domain/components/generalPanel/alarmsAndWarnings/recognizeAlarmModal/ViewInfoModal';
import IconBadge from '@/components/icon/IconBadge';
import installationAlarmPanelFields from '@/domain/data/generalPanel/alarmsAndWarnings/installation-alarm-panel-fields.json';
import localEventBus, {
  events,
} from '@/domain/services/generalPanel/alarmsAndWarnings/localEventBus';
import globalEventBus from '@/services/globalEventBus';
import InfoBadge from '@/components/icon/InfoBadge';
import Classification from '@/components/Classification';

export default {
  components: {
    IconBadge,
    RecognizeAlarmModal,
    DetailsAlarmModal,
    RecognizedAlarmInfoModal,
    BoltCircle,
    AccordionPanel,
    InfoBadge,
    Classification,
  },
  mixins: [LoadingMixin],
  props: {
    expandedInstallation: {
      type: String,
      required: false,
      default: '',
    },
    installation: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      alarms: [],
      modalAlarm: {},
      collapse: true,
    };
  },
  created() {
    globalEventBus.on(
      'shownInstallation.setCollapseController',
      this.setCollapseController,
    );
  },
  beforeDestroy() {
    globalEventBus.off(
      'shownInstallation.setCollapseController',
      this.setCollapseController,
    );
  },
  computed: {
    collapseController: {
      get() {
        return this.collapse || !this.isThisInstallationExpanded;
      },
      set(value) {
        this.collapse = value;
        this.$emit('on-collapse-change', {
          installation: this.installation.installationId,
          value,
        });
      },
    },
    isThisInstallationExpanded() {
      return this.expandedInstallation === this.installation.installationId;
    },
    fields() {
      return installationAlarmPanelFields.map(item => ({
        ...item,
        label: this.$t(item.label),
      }));
    },
    searchFilter() {
      return {
        installationId: this.installation.installationId,
        status: this.filter.generalFilter.selectedAlarmStatus.map(x => x.id),
        alarmClassification: this.filter.generalFilter.selectedAlarmClassifications.map(
          x => x.id,
        ),
        period: this.filter.generalFilter.period,
        assetsIds: this.getAssetsByInstallationId(
          this.installation.installationId,
        ),
      };
    },
    isRecognizeButtonDisabled() {
      return !this.$hasUserPermission('PER003');
    },
  },
  methods: {
    getAssetsByInstallationId(installationId) {
      if (!installationId) {
        return [];
      }

      return (
        this.filter.companyFilter.assets
          .filter(x => x.installation.id === installationId)
          .map(x => x.id) || []
      );
    },
    onShownHandler() {
      globalEventBus.emit(
        'shownInstallation.setCollapseController',
        this.installation.installationId,
      );
    },
    setCollapseController(installationId) {
      if (installationId !== this.expandedInstallation) {
        this.collapseController = true;
      }
    },
    getFormattedSampleDateTime(date) {
      return dateTimeFormatter(
        date,
        formats.DATETIME_SHORT_WITH_SECONDS,
        timezones.UTC,
      );
    },
    getFormattedDateTime(date) {
      return dateTimeFormatter(date, formats.DATETIME_SHORT_WITH_SECONDS, null);
    },
    getAlarmValueTitle(alarm) {
      if (alarm.offlineAlarm) {
        return this.$t('engineering.sample-date');
      }
      return '-';
    },
    isInstallationNormal() {
      const { alarmsSummary } = this.installation;
      const isNormal = this.doesNotHaveAlarms(alarmsSummary);
      if (isNormal) {
        this.collapse = true;
      }
      return isNormal;
    },
    onRecognizeAlarm() {
      this.$emit('recognize-alarm');
    },
    async openRecognizeModal(alarm) {
      this.modalAlarm = {
        id: alarm.id,
        ...(await AlarmsWarningsService.getAlarmDetails(alarm.id)),
        events: [],
      };

      const modalWaiter = !alarm.recognized
        ? this.$refs.recognizeAlarmModal.show()
        : this.$refs.recognizedAlarmInfoModal.show();

      localEventBus.emit(events.MODAL_OPEN, modalWaiter);
    },
    async openDetailsModal(item) {
      this.modalAlarm = {
        id: item.id,
        ...(await AlarmsWarningsService.getAlarmDetails(item.id)),
        events: await AlarmsWarningsService.getAlarmEvents(item.id),
      };
      this.$refs.detailsAlarmModal.show();
    },
    getAlarmIconColor(item) {
      return item.recognized ? 'var(--color-info)' : 'var(--color-secondary)';
    },
    getAlarmColor(alarm) {
      return alarm.acted
        ? 'var(--color-secondary)'
        : 'var(--font-color-primary)';
    },
    getBadgeColor(alarm) {
      return alarm.acted ? 'var(--color-info)' : 'var(--font-color-primary)';
    },
    getFormattedAlarmDateTime(item) {
      return dateTimeFormatter(item.date);
    },
    getAlarmDetails(collapsed, hasLocationSet) {
      if (!hasLocationSet) {
        localEventBus.emit(events.SET_USER_POSITION);
      }
      if (!collapsed) {
        this.startLoading();
        AlarmsWarningsService.getAlarmsDetailsByInstallation(this.searchFilter)
          .then(resp => {
            this.alarms = resp;
          })
          .finally(() => {
            this.endLoading();
            localEventBus.emit(events.END_GET_DESCRIPTION);
          });
      }
    },
    isInstallationBlockedOrDisabled() {
      return this.installation.disabled || this.installation.blocked;
    },
    getInstallationColor() {
      const { alarmsSummary } = this.installation;
      const colorRules = {
        'var(--color-gray-intermediary)': this.isInstallationBlockedOrDisabled(),
        'var(--color-danger)': this.hasActedNotRecognizedAlarm(alarmsSummary),
        'var(--color-info)': this.areAllAlarmsRecognized(alarmsSummary),
        'var(--color-gray-dark)': this.hasNotActedNotRecognizedAlarm(
          alarmsSummary,
        ),
        'var(--color-primary)': this.doesNotHaveAlarms(alarmsSummary),
      };
      const colors = Object.keys(colorRules);
      const attendedRule = colors.find(color => !!colorRules[color]);
      return attendedRule || '';
    },
    hasActedNotRecognizedAlarm(alarmsSummary) {
      return !!alarmsSummary.actedAndNotRecognized;
    },
    areAllAlarmsRecognized(alarmsSummary) {
      return (
        !!alarmsSummary.actedAndRecognized
        && !alarmsSummary.actedAndNotRecognized
      );
    },
    hasNotActedNotRecognizedAlarm(alarmsSummary) {
      return !!alarmsSummary.notActedAndNotRecognized;
    },
    doesNotHaveAlarms(alarmsSummary) {
      return (
        !alarmsSummary.actedAndRecognized
        && !alarmsSummary.notActedAndNotRecognized
        && !alarmsSummary.actedAndNotRecognized
      );
    },
    getActedAndNotRecognizedTranslation({
      actedAndRecognized,
      actedAndNotRecognized,
      notActedAndNotRecognized,
    }) {
      return this.$t('alarms-and-warnings.installation.acted-not-recognized', {
        acted: actedAndRecognized + actedAndNotRecognized,
        notRecognized: actedAndNotRecognized + notActedAndNotRecognized,
      });
    },
    getNotRecognizedTranslation(notRecognized) {
      return this.$t('alarms-and-warnings.installation.not-recognized', {
        notRecognized,
      });
    },
    hasNoActedAlarmsButHasNotRecognized(alarmsSummary) {
      return (
        !alarmsSummary.actedAndRecognized
        && !alarmsSummary.actedAndNotRecognized
        && !!alarmsSummary.notActedAndNotRecognized
      );
    },
    summariesByAlarms() {
      const { alarmsSummary } = this.installation;
      if (this.doesNotHaveAlarms(alarmsSummary)) {
        return this.$t('alarms-and-warnings.installation.normal');
      }
      if (this.hasNoActedAlarmsButHasNotRecognized(alarmsSummary)) {
        return this.getNotRecognizedTranslation(
          alarmsSummary.notActedAndNotRecognized,
        );
      }
      return this.getActedAndNotRecognizedTranslation(alarmsSummary);
    },
    getInstallationSummary() {
      if (this.installation.disabled) {
        return this.$t('alarms-and-warnings.installation.disabled');
      }
      if (this.installation.blocked) {
        return this.$t('alarms-and-warnings.installation.blocked');
      }
      return this.summariesByAlarms();
    },
  },
  watch: {
    installation() {
      this.getAlarmDetails(this.collapse, true);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.installation-panel
  margin-bottom: 10px
  ::v-deep
    .accordion-panel__slot-container
      margin: 0 !important
    &__table-header
      padding: 10px !important
    &__table-cell
      padding: 0.6rem
    .accordion-panel
      &__title-container
        display: flex
        justify-content: space-between
        width: 100%
        align-items: center
  &__classification-icon
    display: flex
    justify-content: center
  &__installation-header-ico-btn, &__installation-header-ico-lbl
    white-space: nowrap
    align-items: center
    display: flex
  &__installation-header-ico-btn
    > span, button
      z-index: 1
  &__installation-ico-network-container
    display: inline-block
  &__installation-ico-network-svg
    height: 15px
    width: 18px
  &__installation-name
    white-space: normal
    margin-bottom: 0
  &__enter-btn
    text-transform: uppercase
    margin-left: 10px
  &__bolt-ico
    flex-shrink: 0
    margin-right: 5px
  &__details-btn
      color: var(--color-secondary) !important
      font-size: 1em
      &__details-arrow
        margin-left: 5px
        transform: rotate(-45deg)
        font-size: 0.9em
        vertical-align: middle
  &__alarm-btn
    &:hover, &:focus
      text-decoration: none
  &__table
    font-size: .83rem
    ::v-deep tbody td
      text-align: center !important
  &__exclamation
    font-size: 1.3rem
  &__no-collapse
    ::v-deep
      .collapse-button.accordion-panel__arrow
        display: none
      .accordion-panel__header
        cursor: default
</style>
